import { useCallback, useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Divider, Header, Message, Segment } from "semantic-ui-react";
import "react-datepicker/dist/react-datepicker.css";
import { OnlineContext } from "../../../context/online-context";
import UsersService from "../../../services/users";
import UserForm from "../form";

/**
 * Audience edit page component
 * @param {object} history
 * @return {*}
 * @constructor
 */
const UserProfilePage = ({ history }) => {
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  const [success, setSuccess] = useState(false);

  const back_url = "/";
  const intl = useIntl();
  const service = new UsersService();
  const { t1UserId: user_id } = useContext(OnlineContext);

  /**
   * navigate user back
   */
  const handleCancel = useCallback(() => {
    history.push(back_url);
  }, [history]);

  /**
   * handle 404
   */
  const handle404 = () => {
    history.push(back_url, {
      action: "error",
      msg: "Error loading user profile",
    });
  };

  /**
   * form submit handler
   * @return {boolean}
   */
  const handleSubmit = async (values) => {
    setLoading(true);

    const { agency_ids, advertiser_ids, ...data } = values;

    try {
      await service.update(user_id, data);
      setSuccess(true);
    } catch (e) {
      setServerError(e.error.message);
    } finally {
      setLoading(false);
    }
  };

  if (user_id < 1) {
    handle404();
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: only update on "user_id" change
  useEffect(() => {
    (async () => {
      try {
        const r = await service.get(user_id);
        setInitialData({
          first_name: r.data.first_name,
          last_name: r.data.last_name,
          phone: r.data.phone,
          status: r.data?.status,
          title: r.data.title,
          username: r.data.username,
        });
      } catch (_) {
        handle404();
      }
    })();
  }, [user_id]);

  if (initialData === null) {
    return (
      <Segment
        className="loading"
        disabled
        tertiary
        textAlign="center"
      >
        &nbsp;
      </Segment>
    );
  }

  return (
    <Segment basic>
      <Header as="h2">
        {intl.formatMessage({
          defaultMessage: "Edit User",
          id: "HEADING_EDIT_USER",
        })}
      </Header>
      <Divider />
      <Message
        content={intl.formatMessage({
          id: "MESSAGE_PROFILE_UPDATED",
          defaultMessage: "Your profile has been updated.",
        })}
        data-testid="profile-success-message"
        hidden={!success}
        size="tiny"
        style={{ marginTop: "10px" }}
        success
      />
      <Message
        content={serverError}
        error
        hidden={!serverError}
        size="tiny"
        style={{ marginTop: "10px" }}
      />
      <UserForm
        formType="profile"
        initialData={initialData}
        initialIndeterminateAgencies={[]}
        loading={loading}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        submitText={intl.formatMessage({
          id: "BTN_SAVE_CHANGES",
          defaultMessage: "Save Changes",
        })}
      />
    </Segment>
  );
};

export default UserProfilePage;
